import DraggableModal from '../../modal/DraggableModal';
import React, {useEffect, useState} from 'react';
import Grid from '@mui/material/Grid';
import _ from 'lodash';
import AgyleDictionaryField from '../../ui/selector/agyleDictionaryField';
import {useDispatch, useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {useClinicalNoteStyles} from '../clinicalNotes';
import {clearNoteFilters, getNotesByNoteFilters, noteFilters, setNoteFilters} from '../../../store/reducers/notes';
import {useWindowSize} from '../../../util/formatter';
import AgyleToggleButtons from '../../ui/buttons/agyleToggleButtons';
import {getConfig} from '../../../util/configUtil';

const ClinicalNoteFilterModal = ({
                                     open,
                                     onClose,
                                     filterOptions,
                                     notesObject,
                                     clinicNotesWindowRef,
                                     pastAttendances,
                                 }) => {
    const dispatch = useDispatch();
    const classes = useClinicalNoteStyles();
    const {t} = useTranslation();

    const [clinicNotesWindowSize, setClinicNotesWindowSize] = React.useState({width: 1, height: 1});
    const [windowWidth, windowHeight] = useWindowSize();

    // Selectors
    const filter = useSelector(noteFilters);

    const [filteredNotes, setFilteredNotes] = useState([]);

    // Config
    const showAllAttendance = getConfig('modules.clinicalNotes.showAllAttendance', false);
    const showAllNotes = !pastAttendances && showAllAttendance;

    // Filters states
    const filterRef = React.useRef(null);
    const [filterBounds, setFilterBounds] = React.useState({
        left: -(windowWidth - 380),
        right: 0,
        top: 0,
        bottom: (windowHeight / 3),
    });

    useEffect(() => {
        setClinicNotesWindowSize({
            width: clinicNotesWindowRef.current.offsetWidth,
            height: clinicNotesWindowRef.current.offsetHeight,
        });
    }, []);

    useEffect(() => {
        setFilteredNotes(dispatch(getNotesByNoteFilters(notesObject, showAllNotes)));
    }, [filter, notesObject]);

    useEffect(() => {
        if (filterRef && !_.isEmpty(filterRef) && !_.isEmpty(filterRef.current)) {
            const infoBar = document.getElementById('note-warning');
            let newBottom = clinicNotesWindowSize.height - filterRef.current.offsetHeight - (infoBar ? (infoBar.offsetHeight + 15) : 0);
            let newLeft = -(clinicNotesWindowSize.width - filterRef.current.offsetWidth - 10);
            if (filterBounds.bottom !== newBottom || filterBounds.left !== newLeft) {
                setFilterBounds({left: newLeft, right: 0, top: 0, bottom: newBottom});
            }
        }
    }, [open, filter]);

    const resetFilers = () => {
        dispatch(clearNoteFilters());
    };

    const handleSetFilerField = (name, value) => {
        dispatch(setNoteFilters({[name]: value}));
    };

    const handleChangeFilter = (event, type) => {
        // eslint-disable-next-line default-case
        switch (type) {
            case 'user':
                dispatch(setNoteFilters({user: event}));
                break;
            case  'spec':
                dispatch(setNoteFilters({spec: event}));
                break;
            case 'type':
                dispatch(setNoteFilters({type: event}));
                break;
            case 'recType':
                if (event && event.length > 0) {
                    handleSetFilerField('status', 'user');
                } else if (filter.status === 'user') {
                    handleSetFilerField('status', 'all');
                }
                dispatch(setNoteFilters({recType: event}));
                break;
        }
    };

    const setStatusNote = (value) => {
        if (value !== 'user' && filter.recType && filter.recType.length > 0) {
            dispatch(setNoteFilters({recType: []}));
        }
        handleSetFilerField('status', value);
    };

    const getResults = () => {
        if (showAllNotes) {
            const allNotes = _.flatten(notesObject.map(n => n.note));
            const allFilteredNotes = _.flatten(filteredNotes.map(n => n.note));
            return `Results: ${allFilteredNotes?.length || 0}/${allNotes?.length || 0}`;
        }
        return `Results: ${filteredNotes.length}/${notesObject.length}`;
    };

    if (!open) return null;

    return (
        <>
            <DraggableModal
                bounds={filterBounds}
                nodeRef={filterRef}
                width={350}
                onClose={onClose}
                onReset={resetFilers}
            >
                <Grid container spacing={1} justifyContent="space-between">
                    <Grid item xs={12} className={classes.results}>
                        {_.isEmpty(filteredNotes) ? 'Results not found' : getResults()}
                    </Grid>

                    <Grid item xs={12}>
                        <AgyleToggleButtons
                            classNameRoot={classes.toggleRoot}
                            classNameLabel={classes.toggleLabel}
                            classNameSelected={classes.toggleSelected}
                            onChange={(e) => {
                                if (e) {
                                    setStatusNote(e);
                                }
                            }}
                            value={filter.status}
                            size="small"
                            options={[
                                {value: 'all', label: 'All notes'},
                                {value: 'system', label: 'Forms notes'},
                                {value: 'user', label: 'Users notes'},
                            ]}
                            minWidth={100}
                            fullWidth
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <AgyleToggleButtons
                            classNameRoot={classes.toggleRoot}
                            classNameLabel={classes.toggleLabel}
                            classNameSelected={classes.toggleSelected}
                            onChange={(e) => {
                                if (e) {
                                    handleSetFilerField('filterNoteType', e);
                                }
                            }}
                            value={filter.filterNoteType}
                            size="small"
                            options={[
                                {value: 'all', label: 'All types'},
                                {value: 'maps', label: 'Body Maps'},
                                {value: 'text', label: 'Text Notes'},
                            ]}
                            minWidth={100}
                            fullWidth
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <AgyleToggleButtons
                            classNameRoot={classes.toggleRoot}
                            classNameLabel={classes.toggleLabel}
                            classNameSelected={classes.toggleSelected}
                            onChange={(e) => {
                                if (e) {
                                    handleSetFilerField('showFirst', e);
                                }
                            }}
                            value={filter.showFirst}
                            size="small"
                            options={[
                                {value: 'all', label: 'Show all'},
                                {value: 'last', label: 'Show most updated'},
                            ]}
                            minWidth={100}
                            fullWidth
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <AgyleDictionaryField
                            size="small"
                            multiple
                            label={t('User')}
                            limitTags={2}
                            options={filterOptions?.users || []}
                            disableCloseOnSelect
                            getOptionLabel={(option) => option}
                            isOptionEqualToValue={(option, value) => option === value}
                            value={filter.user}
                            onChange={(event, newValue) => handleChangeFilter(newValue, 'user')}
                            noBorder={false}
                            withBackground={false}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <AgyleDictionaryField
                            size="small"
                            multiple
                            limitTags={2}
                            options={filterOptions?.spec || []}
                            label={t('Specialty')}
                            getOptionLabel={(option) => option}
                            isOptionEqualToValue={(option, value) => option === value}
                            disableCloseOnSelect
                            value={filter.spec}
                            onChange={(event, newValue) => handleChangeFilter(newValue, 'spec')}
                            noBorder={false}
                            withBackground={false}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <AgyleDictionaryField
                            size="small"
                            multiple
                            limitTags={2}
                            label={t('Note type')}
                            options={filterOptions?.recType || []}
                            getOptionLabel={(option) => option}
                            isOptionEqualToValue={(option, value) => option === value}
                            disableCloseOnSelect
                            value={filter.recType}
                            onChange={(event, newValue) => handleChangeFilter(newValue, 'recType')}
                            noBorder={false}
                            withBackground={false}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <AgyleDictionaryField
                            size="small"
                            multiple
                            limitTags={2}
                            label={t('Form Name')}
                            options={filterOptions?.type || []}
                            getOptionLabel={(option) => option}
                            isOptionEqualToValue={(option, value) => option === value}
                            disableCloseOnSelect
                            value={filter.type}
                            onChange={(event, newValue) => {
                                if (_.isEmpty(newValue)) handleSetFilerField('display', 'show');
                                handleChangeFilter(newValue, 'type');
                            }}
                            noBorder={false}
                            withBackground={false}
                        />
                    </Grid>

                    {!_.isEmpty(filter.type) &&
                        <Grid item xs={12}>
                            <AgyleToggleButtons
                                classNameRoot={classes.toggleRoot}
                                classNameLabel={classes.toggleLabel}
                                classNameSelected={classes.toggleSelected}
                                onChange={(e) => {
                                    if (e) {
                                        handleSetFilerField('display', e);
                                    }
                                }}
                                value={filter.display}
                                size="small"
                                options={[
                                    {value: 'show', label: 'Display Selected'},
                                    {value: 'hide', label: 'Hide Selected'},
                                ]}
                                minWidth={100}
                                fullWidth
                            />
                        </Grid>
                    }
                </Grid>
            </DraggableModal>
        </>
    );
};

export default ClinicalNoteFilterModal;